import React from 'react';
import { useBEOScan } from '../../hooks/useBEOScan';
import { useToasts } from '../../ui/ToastProvider';

// ── Types ───────────────────────────────────────────────────────────────────

interface Props {
  onClose: () => void;
  onJobStarted: (jobId: string, fileName: string) => void;
}

interface FileEntry {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'started' | 'error';
  jobId: string | null;
  error: string | null;
}

type Action =
  | { type: 'add'; files: File[] }
  | { type: 'remove'; id: string }
  | { type: 'set_status'; id: string; status: FileEntry['status']; jobId?: string; error?: string }
  | { type: 'clear_all' };

// ── Constants ───────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = new Set([
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]);
const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB
const ACCEPT_STRING = 'application/pdf,image/jpeg,image/png,image/webp';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_TYPES.has(file.type)) {
    return `Unsupported file type: ${file.type || 'unknown'}. Use PDF, JPEG, PNG, or WEBP.`;
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `File too large (${formatSize(file.size)}). Maximum is 25 MB.`;
  }
  return null;
}

// ── Reducer ─────────────────────────────────────────────────────────────────

function filesReducer(state: FileEntry[], action: Action): FileEntry[] {
  switch (action.type) {
    case 'add': {
      const newEntries: FileEntry[] = action.files.map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        status: 'pending' as const,
        jobId: null,
        error: null,
      }));
      return [...state, ...newEntries];
    }
    case 'remove':
      return state.filter((f) => f.id !== action.id);
    case 'set_status':
      return state.map((f) =>
        f.id === action.id
          ? { ...f, status: action.status, jobId: action.jobId ?? f.jobId, error: action.error ?? f.error }
          : f,
      );
    case 'clear_all':
      return [];
    default:
      return state;
  }
}

// ── Component ───────────────────────────────────────────────────────────────

export default function BEOUploadModal({ onClose, onJobStarted }: Props) {
  const { push: pushToast } = useToasts();
  const beoScan = useBEOScan();
  const [files, dispatch] = React.useReducer(filesReducer, []);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const dragCounterRef = React.useRef(0);

  // ── Drag & Drop handlers ──────────────────────────────────────

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragActive(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragActive(false);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  }

  // ── File addition with validation ─────────────────────────────

  function addFiles(incoming: File[]) {
    const valid: File[] = [];
    for (const file of incoming) {
      const err = validateFile(file);
      if (err) {
        pushToast({ type: 'error', message: `${file.name}: ${err}` });
      } else {
        valid.push(file);
      }
    }
    if (valid.length) dispatch({ type: 'add', files: valid });
  }

  // ── File picker ───────────────────────────────────────────────

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files;
    if (selected?.length) addFiles(Array.from(selected));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── Camera capture ────────────────────────────────────────────

  function handleCameraCapture() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    (input as any).capture = 'environment';
    input.style.display = 'none';
    input.onchange = () => {
      if (input.files?.length) addFiles(Array.from(input.files));
      input.remove();
    };
    document.body.appendChild(input);
    input.click();
  }

  // ── Submit handler ────────────────────────────────────────────

  async function handleStartImport() {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (!pendingFiles.length) return;
    setIsSubmitting(true);

    let startedCount = 0;
    for (const entry of pendingFiles) {
      dispatch({ type: 'set_status', id: entry.id, status: 'uploading' });
      const jobId = await beoScan.startScan(entry.file);
      if (jobId) {
        dispatch({ type: 'set_status', id: entry.id, status: 'started', jobId });
        onJobStarted(jobId, entry.file.name);
        startedCount += 1;
      } else {
        dispatch({ type: 'set_status', id: entry.id, status: 'error', error: beoScan.error || 'Upload failed' });
      }
    }

    setIsSubmitting(false);

    if (startedCount > 0) {
      pushToast({ type: 'success', message: `${startedCount} BEO import${startedCount > 1 ? 's' : ''} started \u2014 you\u2019ll be notified when ready.` });
      onClose();
    }
  }

  // ── Overlay click ─────────────────────────────────────────────

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget && !isSubmitting) onClose();
  }

  // ── Status helpers ────────────────────────────────────────────

  const pendingCount = files.filter((f) => f.status === 'pending').length;
  const hasFiles = files.length > 0;

  const statusIcon: Record<FileEntry['status'], string> = {
    pending: '\u23F3',
    uploading: '\u2B06\uFE0F',
    started: '\u2705',
    error: '\u274C',
  };

  const statusColor: Record<FileEntry['status'], string> = {
    pending: 'var(--text-muted)',
    uploading: '#3B82F6',
    started: '#10B981',
    error: '#EF4444',
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="beo-upload-title"
        style={{ maxWidth: 560 }}
      >
        {/* Header */}
        <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 id="beo-upload-title" style={{ margin: 0, fontSize: '1.1rem' }}>Import BEO</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
            style={{ fontSize: '1.2rem', lineHeight: 1, padding: '0.25rem' }}
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
          {/* Drop zone */}
          <div
            className={`beo-dropzone${isDragActive ? ' beo-dropzone--active' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="region"
            aria-label="Drop files here"
          >
            <div style={{ fontSize: '2rem', marginBottom: 'var(--space-sm)' }}>
              {isDragActive ? '\uD83D\uDCE5' : '\uD83D\uDCC4'}
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--text)', marginBottom: 'var(--space-xs)' }}>
              {isDragActive ? 'Drop files here' : 'Drag & drop BEO files here'}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              PDF, JPEG, PNG, or WEBP &middot; Max 25 MB per file
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm" onClick={() => fileInputRef.current?.click()}>
              Select Files
            </button>
            <button type="button" className="btn btn-sm" onClick={handleCameraCapture}>
              Take Photo
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_STRING}
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
          </div>

          {/* File list */}
          {hasFiles && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
              {files.map((entry) => (
                <div
                  key={entry.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    padding: 'var(--space-sm) var(--space-md)',
                    background: 'var(--surface-soft)',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                  }}
                >
                  <span style={{ fontSize: '0.85rem' }}>{statusIcon[entry.status]}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.file.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: statusColor[entry.status] }}>
                      {entry.status === 'pending' && formatSize(entry.file.size)}
                      {entry.status === 'uploading' && 'Uploading...'}
                      {entry.status === 'started' && 'Import started'}
                      {entry.status === 'error' && (entry.error || 'Failed')}
                    </div>
                    {entry.status === 'uploading' && (
                      <div className="beo-file-progress" style={{ marginTop: 4 }}>
                        <div className="beo-file-progress__bar" />
                      </div>
                    )}
                  </div>
                  {entry.status === 'pending' && !isSubmitting && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={(e) => { e.stopPropagation(); dispatch({ type: 'remove', id: entry.id }); }}
                      aria-label={`Remove ${entry.file.name}`}
                      style={{ fontSize: '0.9rem', padding: '0.1rem 0.3rem', color: 'var(--text-muted)' }}
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleStartImport}
            disabled={!pendingCount || isSubmitting}
          >
            {isSubmitting ? 'Starting...' : `Start Import${pendingCount > 1 ? ` (${pendingCount})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
